import React from 'react';
import styled from 'styled-components';
import {withRouter} from 'react-router-dom';

import {useApi} from '../../lib/api';
import {withUser} from '../../lib/user';
import Card from '../../components/Card';
import ListItem from '../../components/ListItem';
import TimeAgo from '../../components/TimeAgo';
import {T} from '../../components/I18n';

import NoNotificationsIcon from './no-notifications.svg';
import { UserLink } from '../../components/UserLink';

function NotificationList({notifications, history}) {
  const api = useApi();
  // Notifications are owned by the screen, so keep track of the ones read from here to update them in place.
  const [readIds, setReadIds] = React.useState(() => new Set());

  function isRead(notification) {
    return notification.read || readIds.has(notification.id);
  }

  async function markAsRead(notification) {
    try {
      await api.markNotificationAsRead(notification.id);
      setReadIds(prevReadIds => new Set(prevReadIds).add(notification.id));
    }
    catch(e) {
      // Nothing to do. The notification stays unread and can be marked again.
    }
  }

  function SheetRowReviewNotification(notification) {
    return {
      actions: [
        {
          title: 'Review', 
          onClick: () => {
            markAsRead(notification);
            history.push(`/sheet/${notification.metadata.templateId}/${notification.metadata.sheetId}/data/${notification.metadata.sheetRowId}`);
          }
        }
      ],
      render: (
        <T isolate><UserLink user={notification.from} /> submitted data to <strong><T variable="formname">{notification.metadata.templateName}</T></strong> for review.</T>
      )
    }
  }

  function SheetRowSecondLevelReviewNotification(notification) {
    return {
      actions: [
        {
          title: 'Review',
          onClick: () => {
            markAsRead(notification);
            history.push(`/sheet/${notification.metadata.templateId}/${notification.metadata.sheetId}/data/${notification.metadata.sheetRowId}`);
          }
        }
      ],
      render: (
        <T isolate><UserLink user={notification.from} /> approved submitted data to <strong><T variable="formname">{notification.metadata.templateName}</T></strong>. Awaiting second-level review.</T>
      )
    }
  }

  function ScaleErrorNotification(notification) {
    const {scaleId, errorMessage, templateId, scaleSerial} = notification.metadata || {};

    const goTo = to => () => {
      markAsRead(notification);
      history.push(to);
    };

    const actions = [
      {
        title: 'View Scale',
        onClick: goTo(scaleId ? `/devices/scale-configurations/${scaleId}` : '/devices/scale-configurations')
      }
    ];

    if (templateId) {
      actions.push({title: 'View Form', onClick: goTo(`/forms/${templateId}`)});
    }

    // Scale errors are informational, so they can be dismissed without navigating anywhere.
    actions.push({title: 'Mark as Read', onClick: () => markAsRead(notification)});

    return {
      actions,
      render: (
        <T isolate>A scale ({scaleSerial}) reported an error{errorMessage ? <>: <T noTranslate>{errorMessage}</T></> : '.'}</T>
      )
    }
  }

  const notificationRegistry = {
    'sheet_row_review_request': SheetRowReviewNotification,
    'sheet_row_second_level_review_request': SheetRowSecondLevelReviewNotification,
    'scale_error': ScaleErrorNotification
  };

  // The card is a queue of what still needs dealing with rather than a history, so anything already read drops out of
  // it. Types this version of the dashboard cannot render are ignored the same way.
  const supported = notifications.filter(notification =>
      notificationRegistry[notification.type] && !isRead(notification));

  return (
    <CardContainer>
      <Card.Title>Notifications</Card.Title>
      {supported.length < 1 ? (
        <Card.Content>
          <NoNotifications>
            <img src={NoNotificationsIcon} alt="" />
            <h3>All caught up!</h3>
            <p>New notifications will appear here.</p>
          </NoNotifications>
        </Card.Content>
      ) : supported.map(notification => {
        const handler = notificationRegistry[notification.type](notification);

        return (
          <Notification
            key={notification.id}
            unread
            subtitle={<TimeAgo date={notification.timestamp} />}
            options={handler.actions}>
            {handler.render}
          </Notification>
        );
      })}
    </CardContainer>
  );
}

const CardContainer = styled(Card)`
  margin-bottom: 2rem;
`;

const NoNotifications = styled.div`
  text-align: center;

  h3 {
    margin: 1rem 0 0.5rem 0;
    font-size: 1.4rem;
    font-weight: 500;
  }

  p {
    padding: 0.5rem 0;
    margin: 0;
  }
`;

const Notification = styled(ListItem)`
  background: ${props => props.unread ? '#edf2fa' : 'white'};
`;

export default withRouter(withUser(NotificationList));